import {
  persistUTMQueryParamsToString,
  redirectAfterLogout,
} from "@common/utils/router";
import { Indicator, MediaQuery, Navbar, NavLink } from "@mantine/core";
import Link from "next/link";
import { useRouter } from "next/router";
import { ReactElement, useCallback } from "react";
import { INavProps } from "../types";
import { SIDE_NAV_WIDTH } from "./constants";
import IconStar from "@common/components/icons/IconStar";
import IconCoin from "@common/components/icons/IconCoin";
import IconUser from "@common/components/icons/IconUser";
import IconGear from "@common/components/icons/IconGear";
import IconInfo from "@common/components/icons/IconInfo";
import IconBook from "@common/components/icons/IconBook";
import IconVisit from "@common/components/icons/IconVisit";
import IconPage from "@common/components/icons/IconPage";
import IconBell from "@common/components/icons/IconBell";
import { sessionStore } from "@stores/session";
import { useAccount } from "@stores/session/hooks";
import { useGetNotifications } from "@components/notifications/hooks";

export default function SideNav({ opened }: INavProps): ReactElement {
  const router = useRouter();
  const { pathname, query } = router;
  const { plan } = query;

  // session
  const account = useAccount();
  const { refetch: getAccount } = sessionStore.useGetAccount({
    enabled: false,
  });

  // alerts — the badge only shows when there is something to read
  const { data: notifications } = useGetNotifications();
  const hasNotifications = !!notifications?.length;

  // logout
  const { mutate: logout } = sessionStore.useLogout();
  const logoutCallback = useCallback(() => {
    logout(null, {
      onSuccess() {
        redirectAfterLogout(router);
      },
    });
  }, [logout, router]);

  // are we in one of the checkout pages? `/checkout`
  const isNotSuccessStep = !!(plan !== "success");
  const showAccountLink = !!account;

  // pages
  const isSettingsPage = pathname === "/account/manage/settings";
  const isBillingPage = pathname === "/account/manage/billing";
  const isAccountPage = pathname === "/account/manage";
  const isResourcesPage = pathname === "/account/resources";
  const isAlertsPage = pathname === "/account/alerts";
  const isOverviewPage =
    pathname === "/account" ||
    pathname === "/account/new-instance" ||
    (pathname.startsWith("/account") &&
      !isSettingsPage &&
      !isBillingPage &&
      !isAccountPage &&
      !isResourcesPage &&
      !isAlertsPage);

  return (
    <Navbar
      p="md"
      hiddenBreakpoint="sm"
      hidden={!opened}
      width={SIDE_NAV_WIDTH}
    >
      <Navbar.Section grow>
        {account && (
          <>
            {isNotSuccessStep && (
              <>
                <NavLink
                  component={Link}
                  label="Instances"
                  icon={<IconStar />}
                  mb="xs"
                  href={persistUTMQueryParamsToString(router, "/account")}
                  active={isOverviewPage}
                  onClick={() => getAccount()}
                />
                <NavLink
                  component={Link}
                  label="Alerts"
                  icon={
                    <Indicator
                      color="red.3"
                      disabled={!hasNotifications}
                      inline
                      offset={1}
                      size={7}
                    >
                      <IconBell size={16} />
                    </Indicator>
                  }
                  mb="xs"
                  href={persistUTMQueryParamsToString(
                    router,
                    "/account/alerts",
                  )}
                  active={isAlertsPage}
                  onClick={() => getAccount()}
                />
                <NavLink
                  component={Link}
                  label="Billing"
                  icon={<IconCoin />}
                  mb="xs"
                  href={persistUTMQueryParamsToString(
                    router,
                    "/account/manage/billing",
                  )}
                  active={isBillingPage}
                  onClick={() => getAccount()}
                />
                <NavLink
                  component={Link}
                  label="Account Information"
                  icon={<IconUser size={16} />}
                  mb="xs"
                  href={persistUTMQueryParamsToString(
                    router,
                    "/account/manage",
                  )}
                  active={isAccountPage}
                  onClick={() => getAccount()}
                />
                <NavLink
                  component={Link}
                  label="Settings"
                  icon={<IconGear size={16} />}
                  mb="xs"
                  href={persistUTMQueryParamsToString(
                    router,
                    "/account/manage/settings",
                  )}
                  active={isSettingsPage}
                  onClick={() => getAccount()}
                />
              </>
            )}

            {isNotSuccessStep && (
              <>
                <NavLink
                  component="a"
                  label="Docs"
                  icon={<IconBook />}
                  mb="xs"
                  href={persistUTMQueryParamsToString(
                    router,
                    "https://metabase.com/docs",
                  )}
                  target="_blank"
                  rel="noreferrer"
                  rightSection={<IconVisit />}
                  onClick={() => getAccount()}
                />
                <NavLink
                  component={Link}
                  label="Support"
                  icon={<IconInfo />}
                  mb="xs"
                  href={persistUTMQueryParamsToString(
                    router,
                    "https://www.metabase.com/help-premium",
                  )}
                  target="_blank"
                  rel="noreferrer"
                  rightSection={<IconVisit />}
                  onClick={() => getAccount()}
                />
              </>
            )}

            {isNotSuccessStep && (
              <NavLink
                component={Link}
                label="Resources"
                icon={<IconPage size={18} />}
                mb="xs"
                href={persistUTMQueryParamsToString(
                  router,
                  "/account/resources",
                )}
                active={isResourcesPage}
                onClick={() => getAccount()}
              />
            )}
          </>
        )}
      </Navbar.Section>

      {showAccountLink && (
        <MediaQuery largerThan="sm" styles={{ display: "none" }}>
          <Navbar.Section>
            <NavLink
              data-testid="nav-link-logout"
              label="Logout"
              icon={undefined}
              onClick={logoutCallback}
            />
          </Navbar.Section>
        </MediaQuery>
      )}
    </Navbar>
  );
}
